import React, { useEffect, useRef, useState } from "react";
import { useHomeViewModel } from "../viewmodel/useHomeViewModel";
import { StatItem } from "../components/StatItem";
import "./Home.scss";
import "../../calls/pages/CallsPage.scss";
import { slugify } from "@/core/utils/stringUtils";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { createRoute, ROUTES } from "@/core/constants/app";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import Tracking from "@/core/utils/tracking";

import { $userBasicInfo, $userProfile } from "@/features/user/store";
import { useUnit } from "effector-react";
import { formatRelativeTime } from "@/core/utils/format";
import { Tags } from "@/core/components/tags/Tags";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { imageList } from "@/core/utils/imageList";
import { Idea } from "@/features/ideas/model/types";
import { $goalsStore, fetchGoalsRequested } from "@/features/goals/store";
import { setModalIsOpened } from "@/core/store/app";
import { CreateGoalModal } from "@/features/goals/components/CreateGoalModal";
import { homeService } from "../service/api";
import { ProfileViewsModal } from "../components/ProfileViewsModal";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { ideasService } from "@/features/ideas/service/api";
import { BlogPost } from "../types/homePageTypes";
import { useCallsViewModel } from "@/features/calls/viewmodel/useCallsViewModel";
import { updateCallBookmark } from "@/features/calls/store";

interface UserViews {
  id: string;
  logo_uri: string;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  description: string;
  job_title: string;
  is_followed_by_me: boolean;
  is_following_me: boolean;
  is_me: boolean;
}

export const Home = () => {
  const { dashboard, loading, error, refetch } = useHomeViewModel();
  const [currentPartnerIndex, setCurrentPartnerIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showAllGoals, setShowAllGoals] = useState(false);
  const partnersRef = useRef<HTMLDivElement>(null);
  const [userBasicInfo, userProfile] = useUnit([$userBasicInfo, $userProfile]);
  const { pro_user, uid } = userBasicInfo || {};
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);
  const insightsInterval = useRef<NodeJS.Timeout | null>(null);
  const [myIdeas, setMyIdeas] = useState<Idea[]>([]);
  const [userViews, setUserViews] = useState<UserViews[]>([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [userViewsLoading, setUserViewsLoading] = useState(false);
  const { goals } = useUnit($goalsStore);
  const fetchGoals = useUnit(fetchGoalsRequested);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isProfileViewsModalOpen, setIsProfileViewsModalOpen] = useState(false);
  const [mobileAvatarError, setMobileAvatarError] = useState(false);
  const [desktopAvatarError, setDesktopAvatarError] = useState(false);

  // ── Blog state ──────────────────────────────────────────────────────────────
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [currentBlogIndex, setCurrentBlogIndex] = useState(0);
  const [blogCardsPerSlide, setBlogCardsPerSlide] = useState(
    window.innerWidth <= 768 ? 1 : window.innerWidth <= 1024 ? 2 : 3,
  );

  const userPictureUrl =
    userProfile?.user_data?.user_picture &&
    Array.isArray(userProfile.user_data.user_picture) &&
    userProfile.user_data.user_picture.length > 0
      ? userProfile.user_data.user_picture[0].url
      : null;

  useEffect(() => {
    setMobileAvatarError(false);
    setDesktopAvatarError(false);
  }, [userPictureUrl]);

  useEffect(() => {
    Tracking.pageView(
      "Homepage",
      location.pathname,
      undefined,
      undefined,
      undefined,
      document.title,
      undefined,
      undefined,
    );
  }, []);

  useEffect(() => {
    fetchGoals(undefined);
  }, [fetchGoals]);

  const handleCreateGoal = () => {
    setIsCreateModalOpen(true);
  };

  // Auto-scroll insights carousel
  useEffect(() => {
    if (!dashboard?.europe_insights || dashboard.europe_insights.length <= 1)
      return;

    insightsInterval.current = setInterval(() => {
      setCurrentInsightIndex((prevIndex) =>
        prevIndex === dashboard.europe_insights.length - 1 ? 0 : prevIndex + 1,
      );
    }, 30000);

    return () => {
      if (insightsInterval.current) {
        clearInterval(insightsInterval.current);
      }
    };
  }, [dashboard?.europe_insights]);

  // Fetch my ideas, user views, and blog posts
  useEffect(() => {
    const fetchMyIdeas = async () => {
      setIdeasLoading(true);
      try {
        const response = await ideasService.getAllIdeas(
          { page: 1, limit: 6 },
          "my-ideas-published",
        );
        setMyIdeas(response.data || []);
      } catch (error) {
        console.error("Error fetching my ideas:", error);
      } finally {
        setIdeasLoading(false);
      }
    };

    const fetchUserViews = async () => {
      setUserViewsLoading(true);
      try {
        const response = await homeService.getUserViews();
        setUserViews(response.data || []);
      } catch (error) {
        console.error("Error fetching my User views:", error);
      } finally {
        setUserViewsLoading(false);
      }
    };

    const fetchBlogPosts = async () => {
      setBlogLoading(true);
      try {
        const posts = await homeService.getLatestPosts();
        setBlogPosts(posts || []);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setBlogLoading(false);
      }
    };

    fetchMyIdeas();
    fetchUserViews();
    fetchBlogPosts();
  }, []);

  const [partnersPerSlide, setPartnersPerSlide] = useState(
    window.innerWidth <= 768 ? 1 : window.innerWidth <= 1024 ? 2 : 3,
  );

  useEffect(() => {
    const handleResize = () => {
      const perSlide =
        window.innerWidth <= 768 ? 1 : window.innerWidth <= 1024 ? 2 : 3;
      setPartnersPerSlide(perSlide);
      setBlogCardsPerSlide(perSlide);
      setCurrentPartnerIndex(0);
      setCurrentBlogIndex(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalSlides = dashboard?.partner_suggestions
    ? Math.ceil(dashboard?.partner_suggestions.length / partnersPerSlide)
    : 0;

  // Blog carousel helpers
  const totalBlogSlides = Math.ceil(blogPosts.length / blogCardsPerSlide);

  const handlePreviousBlog = () =>
    setCurrentBlogIndex((prev) => (prev > 0 ? prev - 1 : totalBlogSlides - 1));

  const handleNextBlog = () =>
    setCurrentBlogIndex((prev) => (prev < totalBlogSlides - 1 ? prev + 1 : 0));

  // Calculate max count from both call and partner activities
  const getMaxActivityCount = () => {
    if (!dashboard?.activities) return 0;

    const callMaxCount =
      dashboard.activities.call && dashboard.activities.call.length > 0
        ? Math.max(...dashboard.activities.call.map((item) => item.count))
        : 0;

    const partnerMaxCount =
      dashboard.activities.partner && dashboard.activities.partner.length > 0
        ? Math.max(...dashboard.activities.partner.map((item) => item.count))
        : 0;

    return Math.max(callMaxCount, partnerMaxCount);
  };

  const maxActivityCount = getMaxActivityCount();

  const calculatePercentage = (count: number) => {
    if (maxActivityCount === 0) return 0;
    return Math.round((count / maxActivityCount) * 100);
  };

  const handlePreviousPartner = () => {
    setCurrentPartnerIndex((prev) => (prev > 0 ? prev - 1 : totalSlides - 1));
  };

  const handleNextPartner = () => {
    setCurrentPartnerIndex((prev) => (prev < totalSlides - 1 ? prev + 1 : 0));
  };

  if (loading) {
    return (
      <div className="home-page">
        <div className="pageTitle">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-page">
        <div className="pageTitle">Error loading dashboard</div>
        <p>{error}</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  function handleBookmarkAction(id: string, is_call_bookmarked: boolean): void {
    updateCallBookmark({ callId: id, isBookmarked: !is_call_bookmarked });
  }

  const getDaysLeftInfo = (deadline: string) => {
    try {
      if (!deadline)
        return { daysLeft: 0, cssClass: "days-yellow", text: "No deadline" };

      const now = new Date();
      const targetDate = new Date(deadline);

      if (isNaN(targetDate.getTime())) {
        return { daysLeft: 0, cssClass: "days-yellow", text: "Invalid date" };
      }

      const diffTime = targetDate.getTime() - now.getTime();
      const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const cssClass = daysLeft > 39 ? "days-green" : "days-yellow";
      const text = `${daysLeft} days left`;

      return { daysLeft, cssClass, text };
    } catch (error) {
      console.warn("Days calculation error:", error);
      return { daysLeft: 0, cssClass: "days-yellow", text: "Error" };
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 67) return "#067647";
    if (66 >= score && score >= 34) return "#f97316";
    return "#ef4444";
  };

  return (
    <div className="home-page">
      <div className="pageTitle">Welcome</div>
      <div className="home-page-wrapper">
        {dashboard && (
          <div className="dashboard-stats">
            <div className="dashboard-section stats-overview">
              <StatItem
                icon={<SvgIcon icon="icon-home-saved" />}
                title="Saved calls"
                value={dashboard.saved_calls}
                hasLink={false}
                onClick={() => {}}
              />
              <StatItem
                icon={<SvgIcon icon="icon-home-declared" />}
                title="Declared interest"
                value={dashboard.declared_interest}
                hasLink={false}
                onClick={() => {}}
              />
              <StatItem
                icon={<i className="pi pi-eye"></i>}
                title="Profile views"
                value={dashboard.profile_views}
                hasLink={true}
                onClick={() => {
                  setIsProfileViewsModalOpen(true);
                  // console.log(userViews, "userViews");
                }}
              />
            </div>
            <div className="showMobile">
              <div className="profileBox">
                <div className="profileBox-row1">
                  <div className="profileBox-avatar">
                    {userPictureUrl && !mobileAvatarError ? (
                      <img
                        src={userPictureUrl}
                        alt={dashboard.user_info.name}
                        className="profile-avatar-image"
                        onError={() => setMobileAvatarError(true)}
                        onLoad={(e) => e.currentTarget.classList.add("loaded")}
                      />
                    ) : (
                      <BgImage img={imageList.noimage} />
                    )}
                  </div>
                  <div className="profileBox-info">
                    <div className="profileBox-name">
                      {dashboard.user_info.name}
                    </div>
                    <div className="profileBox-title">
                      {dashboard.user_info.job_title}
                    </div>

                    <div className="profileBox-stats">
                      <div className="profileBox-stats-item">
                        <div className="profileBox-stats-item-title">
                          Followers
                        </div>
                        <div className="profileBox-stats-item-number">
                          {dashboard.user_info.followers_count}
                        </div>
                      </div>
                      <div className="profileBox-stats-item">
                        <div className="profileBox-stats-item-title">
                          Following
                        </div>
                        <div className="profileBox-stats-item-number">
                          {dashboard.user_info.following_count}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="profileBox-row2">
                  <Link to={`/users/${uid}`} className="profileLink">
                    View profile
                  </Link>
                </div>
              </div>
            </div>
            <div className="dashboard-section">
              <div className="calls-for-you">
                <h3>Newest calls For You</h3>
                <div className="homecalls-list grid-view">
                  {dashboard.calls_for_you?.map((call) => {
                    const daysInfo = getDaysLeftInfo(call.deadline);
                    return (
                      <div
                        key={`${slugify(call.title)}-${call.id}`}
                        className="call-item eu-card"
                      >
                        <Link
                          to={createRoute(ROUTES.CALL_DETAIL, {
                            id: call.id,
                          })}
                        >
                          <div className="call-header">
                            <div className="call-type">
                              <div className="eu-flag">
                                <img src={call.programme.logo} alt="EU call" />
                              </div>
                              <span>{call.programme.name}</span>
                            </div>
                            <button
                              className={`bookmark-btn ${
                                call.is_call_bookmarked ||
                                call.is_call_declared_interest
                                  ? "bookmarked"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleBookmarkAction(
                                  call.id,
                                  call.is_call_bookmarked ||
                                    call.is_call_declared_interest,
                                );
                              }}
                            >
                              <SvgIcon
                                icon={
                                  call.is_call_declared_interest
                                    ? "icon-declare"
                                    : call.is_call_bookmarked
                                      ? "icon-bookmarked"
                                      : "icon-bookmark"
                                }
                                className={
                                  call.is_call_declared_interest
                                    ? "declare-icon"
                                    : ""
                                }
                              />
                            </button>
                          </div>

                          <div className="call-content">
                            <div className="call-badges">
                              {call.is_new && (
                                <span className="badge new-badge">New</span>
                              )}
                              <span className="badge deadline-badge">
                                <SvgIcon icon="icon-clock" /> until{" "}
                                {new Date(call.deadline).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                              </span>
                              <span
                                className={`badge days-left-badge ${daysInfo.cssClass}`}
                              >
                                {daysInfo.text}
                              </span>
                            </div>

                            <h3 className="call-title">
                              <Link
                                to={createRoute(ROUTES.CALL_DETAIL, {
                                  id: call.id,
                                })}
                              >
                                {call.title}
                              </Link>
                            </h3>

                            <div className="call-budget">
                              € {parseInt(call.budget).toLocaleString()}
                            </div>

                            <div className="interested-partners">
                              <span className="partners-label">
                                Interested Partners
                              </span>
                              {(!call?.interested_partners ||
                                call?.interested_partners.length === 0) && (
                                <span className="no-partners">0</span>
                              )}
                              {call?.interested_partners &&
                                call?.interested_partners?.length > 0 && (
                                  <div className="partners-avatars">
                                    {call?.interested_partners
                                      ?.slice(0, 3)
                                      .map((p, i) => (
                                        <div
                                          key={p.id || i}
                                          className="partner-avatar-wrapper"
                                        >
                                          <div className="company-logo">
                                            <BgImage
                                              img={p.logo_uri}
                                              altImage={imageList.noimage}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    {call.interested_partners_count &&
                                      call.interested_partners_count > 3 && (
                                        <span className="more-count">
                                          +{call.interested_partners_count - 3}
                                        </span>
                                      )}
                                  </div>
                                )}
                            </div>

                            <div className="sectors-tags">
                              {call.sectors.slice(0, 4).map((sector) => (
                                <span
                                  key={`${call.id}-${slugify(sector.name)}-${sector.id}`}
                                  className="sector-tag"
                                >
                                  {sector.name}
                                </span>
                              ))}
                              {call.sectors.length > 4 && (
                                <span className="sector-tag more-count">
                                  +{call.sectors.length - 4}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="call-footer">
                            {typeof call?.max_score === "number" ? (
                              <div
                                className="match-badge"
                                style={{
                                  backgroundColor: getScoreColor(
                                    call.max_score,
                                  ),
                                }}
                              >{`${call?.max_score}% match`}</div>
                            ) : (
                              <div></div>
                            )}
                            <div className="call-stats">
                              <div className="stat-item">
                                <SvgIcon icon="icon-message" />{" "}
                                {call.comments_count} comments
                              </div>
                              <div className="stat-item">
                                <SvgIcon icon="icon-ideas" /> {call.ideas_count}{" "}
                                ideas
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="viewAll">
                <Link to="/calls/for-you" className="viewAllAction">
                  View all
                </Link>
              </div>
              <div className="showMobile">
                <div className="highlights ">
                  <div className="highlights-header">
                    <div className="highlights-header-title">
                      Highlights from your field of expertise
                    </div>
                    <div className="highlights-header-tabList">
                      <div
                        className={`highlights-header-tabList-item ${activeTab === 0 ? "active" : ""}`}
                        onClick={() => setActiveTab(0)}
                      >
                        Calls
                      </div>
                      <div
                        className={`highlights-header-tabList-item ${activeTab === 1 ? "active" : ""}`}
                        onClick={() => setActiveTab(1)}
                      >
                        Partners
                      </div>
                    </div>
                  </div>
                  <div className="highlights-results">
                    {activeTab === 0 ? (
                      <div className="highlights-results-calls">
                        {dashboard.activities &&
                          dashboard.activities.call &&
                          dashboard.activities.call.length > 0 && (
                            <div className="highlights-list">
                              {dashboard.activities.call
                                .slice(
                                  0,
                                  showAllActivities
                                    ? dashboard.activities.call.length
                                    : 5,
                                )
                                .map((item, i) => (
                                  <div className="highlights-list-item" key={i}>
                                    <div className="highlights-list-item-term">
                                      {item.term}
                                    </div>
                                    <div className="highlights-list-item-stats">
                                      <div
                                        className="highlights-list-item-percentage"
                                        style={{
                                          width: `${calculatePercentage(item.count)}%`,
                                        }}
                                      ></div>
                                      <div className="highlights-list-item-count">
                                        {item.count}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="highlights-results-partner">
                        {dashboard.activities &&
                          dashboard.activities.partner &&
                          dashboard.activities.partner.length > 0 && (
                            <div className="highlights-list">
                              {dashboard.activities.partner
                                .slice(
                                  0,
                                  showAllActivities
                                    ? dashboard.activities.partner.length
                                    : 5,
                                )
                                .map((item, i) => (
                                  <div className="highlights-list-item" key={i}>
                                    <div className="highlights-list-item-term">
                                      {item.term}
                                    </div>
                                    <div className="highlights-list-item-stats">
                                      <div
                                        className="highlights-list-item-percentage"
                                        style={{
                                          width: `${calculatePercentage(item.count)}%`,
                                        }}
                                      ></div>
                                      <div className="highlights-list-item-count">
                                        {item.count}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                  <div
                    className="showMore"
                    onClick={() => setShowAllActivities(!showAllActivities)}
                    style={{ cursor: "pointer" }}
                  >
                    {showAllActivities ? "Show less" : "Show more"}
                  </div>
                </div>
              </div>
              <div className="showMobile">
                <div className="highlights">
                  <div className="highlights-header">
                    <div className="highlights-header-title">
                      Your funding goals
                    </div>
                    {pro_user && (
                      <div
                        onClick={handleCreateGoal}
                        className="highlights-header-link"
                      >
                        Create new goal
                      </div>
                    )}
                  </div>
                  <div className="highlights-goalResults">
                    {!pro_user ? (
                      <UpgradePrompt
                        title="Create funding goals that match your interests"
                        description="Get full access to all Pro features with a paid plan"
                        buttonText="Upgrade now"
                        buttonLink="/settings/subscription"
                      />
                    ) : goals && goals.length > 0 ? (
                      <div className="funding-goals-list">
                        {goals
                          .slice(0, showAllGoals ? goals.length : 3)
                          .map((goal) => (
                            <div key={goal.id} className="funding-goal-item">
                              <div className="funding-goal-title">
                                {goal.title}
                              </div>
                              <Link
                                to={`/goals/${goal.id}/matches`}
                                className="funding-goal-action"
                              >
                                Find calls{" "}
                                <i className="pi pi-chevron-right"></i>
                              </Link>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="no-goals">
                        <p>You haven't created any funding goals yet.</p>
                        <Link to={ROUTES.GOALS} className="create-goal-link">
                          Create your first goal
                        </Link>
                      </div>
                    )}
                  </div>
                  {pro_user && goals && goals.length > 3 && (
                    <div
                      className="showMore"
                      onClick={() => setShowAllGoals(!showAllGoals)}
                      style={{ cursor: "pointer" }}
                    >
                      {showAllGoals ? "Show less" : "Show more"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {dashboard && (
          <div className="dashboard-sidebar">
            <div className="showDesktop">
              <div className="profileBox">
                <div className="profileBox-row1">
                  <div className="profileBox-avatar">
                    {userPictureUrl && !mobileAvatarError ? (
                      <img
                        src={userPictureUrl}
                        alt={dashboard.user_info.name}
                        className="profile-avatar-image"
                        onError={() => setMobileAvatarError(true)}
                        onLoad={(e) => e.currentTarget.classList.add("loaded")}
                      />
                    ) : (
                      <BgImage img={imageList.noimage} />
                    )}
                  </div>
                  <div className="profileBox-info">
                    <div className="profileBox-name">
                      {dashboard.user_info.name}
                    </div>
                    <div className="profileBox-title">
                      {dashboard.user_info.job_title}
                    </div>

                    <div className="profileBox-stats">
                      <div className="profileBox-stats-item">
                        <div className="profileBox-stats-item-title">
                          Followers
                        </div>
                        <div className="profileBox-stats-item-number">
                          {dashboard.user_info.followers_count}
                        </div>
                      </div>
                      <div className="profileBox-stats-item">
                        <div className="profileBox-stats-item-title">
                          Following
                        </div>
                        <div className="profileBox-stats-item-number">
                          {dashboard.user_info.following_count}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="profileBox-row2">
                  <Link to={`/users/${uid}`} className="profileLink">
                    View profile
                  </Link>
                </div>
              </div>
            </div>
            <div className="showDesktop">
              <div className="highlights">
                <div className="highlights-header">
                  <div className="highlights-header-title">
                    Highlights from your field of expertise
                  </div>
                  <div className="highlights-header-tabList">
                    <div
                      className={`highlights-header-tabList-item ${activeTab === 0 ? "active" : ""}`}
                      onClick={() => setActiveTab(0)}
                    >
                      Calls
                    </div>
                    <div
                      className={`highlights-header-tabList-item ${activeTab === 1 ? "active" : ""}`}
                      onClick={() => setActiveTab(1)}
                    >
                      Partners
                    </div>
                  </div>
                </div>
                <div className="highlights-results">
                  {activeTab === 0 ? (
                    <div className="highlights-results-calls">
                      {dashboard.activities &&
                        dashboard.activities.call &&
                        dashboard.activities.call.length > 0 && (
                          <div className="highlights-list">
                            {dashboard.activities.call
                              .slice(
                                0,
                                showAllActivities
                                  ? dashboard.activities.call.length
                                  : 5,
                              )
                              .map((item, i) => (
                                <div className="highlights-list-item" key={i}>
                                  <div className="highlights-list-item-term">
                                    {item.term}
                                  </div>
                                  <div className="highlights-list-item-stats">
                                    <div
                                      className="highlights-list-item-percentage"
                                      style={{
                                        width: `${calculatePercentage(item.count)}%`,
                                      }}
                                    ></div>
                                    <div className="highlights-list-item-count">
                                      {item.count}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                    </div>
                  ) : (
                    <div className="highlights-results-partner">
                      {dashboard.activities &&
                        dashboard.activities.partner &&
                        dashboard.activities.partner.length > 0 && (
                          <div className="highlights-list">
                            {dashboard.activities.partner
                              .slice(
                                0,
                                showAllActivities
                                  ? dashboard.activities.partner.length
                                  : 5,
                              )
                              .map((item, i) => (
                                <div className="highlights-list-item" key={i}>
                                  <div className="highlights-list-item-term">
                                    {item.term}
                                  </div>
                                  <div className="highlights-list-item-stats">
                                    <div
                                      className="highlights-list-item-percentage"
                                      style={{
                                        width: `${calculatePercentage(item.count)}%`,
                                      }}
                                    ></div>
                                    <div className="highlights-list-item-count">
                                      {item.count}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                    </div>
                  )}
                </div>
                <div
                  className="showMore"
                  onClick={() => setShowAllActivities(!showAllActivities)}
                  style={{ cursor: "pointer" }}
                >
                  {showAllActivities ? "Show less" : "Show more"}
                </div>
              </div>
            </div>

            {/* Your Funding Goals Section */}
            <div className="showDesktop">
              <div className="highlights">
                <div className="highlights-header">
                  <div className="highlights-header-title">
                    Your funding goals
                  </div>
                  {pro_user && (
                    <div
                      onClick={handleCreateGoal}
                      className="highlights-header-link"
                    >
                      Create new goal
                    </div>
                  )}
                </div>
                <div className="highlights-goalResults">
                  {!pro_user ? (
                    <UpgradePrompt
                      title="Create funding goals that match your interests"
                      description="Get full access to all Pro features with a paid plan"
                      buttonText="Upgrade now"
                      buttonLink="/settings/subscription"
                    />
                  ) : goals && goals.length > 0 ? (
                    <div className="funding-goals-list">
                      {goals
                        .slice(0, showAllGoals ? goals.length : 3)
                        .map((goal) => (
                          <div key={goal.id} className="funding-goal-item">
                            <div className="funding-goal-title">
                              {goal.title}
                            </div>
                            <Link
                              to={`/goals/${goal.id}/matches`}
                              className="funding-goal-action"
                            >
                              Find calls <i className="pi pi-chevron-right"></i>
                            </Link>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="no-goals">
                      <p>You haven't created any funding goals yet.</p>
                      <Link to={ROUTES.GOALS} className="create-goal-link">
                        Create your first goal
                      </Link>
                    </div>
                  )}
                </div>
                {pro_user && goals && goals.length > 3 && (
                  <div
                    className="showMore"
                    onClick={() => setShowAllGoals(!showAllGoals)}
                    style={{ cursor: "pointer" }}
                  >
                    {showAllGoals ? "Show less" : "Show more"}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {dashboard && (
        <div className="suggestedPartners">
          <div className="partners-section">
            <div className="partners-header">
              <h3>Partner suggestions</h3>
              <div className="navigation-arrows">
                <Button
                  icon="pi pi-chevron-left"
                  text
                  className="nav-arrow"
                  onClick={handlePreviousPartner}
                  disabled={totalSlides <= 1}
                />
                <Button
                  icon="pi pi-chevron-right"
                  text
                  className="nav-arrow"
                  onClick={handleNextPartner}
                  disabled={totalSlides <= 1}
                />
              </div>
            </div>

            <div className="partners-carousel">
              {dashboard.partner_suggestions.length > 0 ? (
                <div className="partners-track" ref={partnersRef}>
                  {dashboard.partner_suggestions
                    .slice(
                      currentPartnerIndex * partnersPerSlide,
                      (currentPartnerIndex + 1) * partnersPerSlide,
                    )
                    .map((partner) => (
                      <div key={partner.id} className="partner-item eu-card">
                        <div className="partner-header">
                          <div className="partner-logo">
                            <img
                              src={
                                partner.logo_uri && partner.logo_uri !== ""
                                  ? partner.logo_uri
                                  : imageList.noimage
                              }
                              alt={partner.title}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                                const placeholder =
                                  target.nextElementSibling as HTMLElement;
                                if (placeholder)
                                  placeholder.style.display = "flex";
                              }}
                            />
                            <div
                              className="partner-logo-placeholder"
                              style={{
                                display: imageList.noimage ? "none" : "flex",
                              }}
                            >
                              <span>{partner.title?.[0] || "?"}</span>
                            </div>
                          </div>
                          <div className="partner-info">
                            <h3 className="partner-title">
                              <Link to={`/partner/${partner.id}`}>
                                {partner.title}
                              </Link>
                            </h3>
                            <div className="partner-meta">
                              {partner.partner_type && (
                                <div className="meta-item">
                                  <SvgIcon icon="icon-building" />
                                  <span>{partner.partner_type.name}</span>
                                </div>
                              )}
                              {partner.country && (
                                <div className="meta-item">
                                  <SvgIcon icon="icon-location" />
                                  <span>{partner.country.name}</span>
                                </div>
                              )}
                              <div className="meta-item">
                                <SvgIcon icon="icon-clock" />
                                <span>
                                  {partner.since ||
                                    (partner.field_created
                                      ? new Date(
                                          parseInt(partner.field_created) *
                                            1000,
                                        )
                                          .getFullYear()
                                          .toString()
                                      : "N/A")}
                                </span>
                              </div>
                              {partner.size && (
                                <div className="meta-item">
                                  <SvgIcon icon="icon-users" />
                                </div>
                              )}
                              <div className="meta-item">
                                <SvgIcon icon="icon-projects" />
                                <span>
                                  {partner.implemented_projects || 0} projects
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="partner-sectors">
                          {partner.sectors &&
                            Array.isArray(partner.sectors) &&
                            partner.sectors.slice(0, 3).map((sector) => (
                              <span key={sector.id} className="sector-tag">
                                {sector.name}
                              </span>
                            ))}
                          {partner.sectors && partner.sectors.length > 3 && (
                            <span className="sector-tag more-count">
                              +{partner.sectors.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="no-partners-placeholder">
                  <p>No interested partners yet</p>
                </div>
              )}
            </div>
          </div>
          <div className="viewAll">
            <Link to="/partners" className="viewAllAction">
              View all
            </Link>
          </div>
        </div>
      )}

      <div className="home-page-wrapper">
        <div className="dashboard-stats">
          <div className="my-ideas-section">
            <h3>Your Ideas</h3>
            {ideasLoading ? (
              <div className="ideas-loading">Loading ideas...</div>
            ) : myIdeas.length > 0 ? (
              <div className="ideas-grid">
                {myIdeas.map((idea) => (
                  <div key={idea.id} className="idea-card eu-card">
                    {idea.status && (
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          paddingBottom: "1rem",
                        }}
                      >
                        <span
                          className={`idea-status status-${idea.status.toLowerCase()}`}
                        >
                          <Tags
                            type={"custom"}
                            className="badgeGreen"
                            text={idea.status}
                          />
                        </span>
                        {idea.is_member && !idea.is_owner && (
                          <span
                            className={`idea-status status-${idea.status.toLowerCase()}`}
                          >
                            <Tags
                              type={"custom"}
                              className="badgeGreen"
                              text={"Member"}
                            />
                          </span>
                        )}
                        {idea.is_owner && (
                          <span
                            className={`idea-status status-${idea.status.toLowerCase()}`}
                          >
                            <Tags
                              type={"custom"}
                              className="round"
                              text={"Owner"}
                            />
                          </span>
                        )}
                      </div>
                    )}
                    <div className="idea-header">
                      <h4 className="idea-title">
                        <Link
                          to={createRoute(ROUTES.IDEAS_DETAIL, { id: idea.id })}
                        >
                          {idea.title}
                        </Link>
                      </h4>
                    </div>
                    <div className="idea-content">
                      {idea.description && (
                        <p className="idea-description">
                          {idea.description.length > 100
                            ? `${idea.description.substring(0, 100)}...`
                            : idea.description}
                        </p>
                      )}
                      {idea.sectors && idea.sectors.length > 0 && (
                        <div className="idea-sectors">
                          {idea.sectors.slice(0, 3).map((sector) => (
                            <span key={sector.id} className="sector-tag">
                              {sector.name}
                            </span>
                          ))}
                          {idea.sectors.length > 3 && (
                            <span className="sector-tag more-count">
                              +{idea.sectors.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="idea-footer">
                      <div className="idea-stats">
                        {idea.comments_count !== undefined && (
                          <span className="stat-item">
                            <SvgIcon icon="icon-message" />{" "}
                            {idea.comments_count}
                          </span>
                        )}
                        {idea.members && idea.members.length > 0 && (
                          <span className="stat-item">
                            <SvgIcon icon="icon-users" /> {idea.members.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="viewAll">
                  <Link to="/ideas/my-ideas" className="viewAllAction">
                    View all
                  </Link>
                </div>
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <div className="ideasNoContent">
                  {typeof userBasicInfo?.partner === "string" &&
                  userBasicInfo.partner === "freelancer" ? (
                    <div className="ideasNoContent-icon">
                      <SvgIcon icon="icon-idea-org" />
                    </div>
                  ) : (
                    <div className="ideasNoContent-icon">
                      <SvgIcon icon="icon-idea" />
                    </div>
                  )}
                  <div className="ideasNoContent-title">
                    {typeof userBasicInfo?.partner === "string" &&
                    userBasicInfo.partner === "freelancer"
                      ? "Create your organisation to be able to share or join an idea"
                      : "You haven't created or joined an idea yet."}
                  </div>
                  <div className="ideasNoContent-desc">
                    {typeof userBasicInfo?.partner === "string" &&
                    userBasicInfo.partner === "freelancer"
                      ? "This feature is only available to users with an organisation"
                      : "Start your idea by declaring interest in a call or request to join an existing idea."}
                  </div>
                  {typeof userBasicInfo?.partner === "string" &&
                  userBasicInfo.partner === "freelancer" ? (
                    <div className="ideasNoContent-actions">
                      <Link
                        to="/settings/organization"
                        className="ideasNoContent-actions-btnIdeas"
                      >
                        Go to settings
                      </Link>
                    </div>
                  ) : (
                    <div className="ideasNoContent-actions">
                      <Link
                        to="/calls"
                        className="ideasNoContent-actions-btnCalls"
                      >
                        View all calls
                      </Link>
                      <Link
                        to="/ideas"
                        className="ideasNoContent-actions-btnIdeas"
                      >
                        View all ideas
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="showMobile">
            <section className="insights">
              <div className="rocket">
                <img src={imageList.rocket} alt="Insights" />
              </div>

              <div className="insights-title">Europe now</div>
              <div className="insights-textList">
                {dashboard?.europe_insights &&
                  dashboard.europe_insights.length > 0 && (
                    <div className="insights-carousel">
                      <div
                        className="insights-carousel-track"
                        style={{
                          transform: `translateX(-${currentInsightIndex * 100}%)`,
                          transition: "transform 0.5s ease-in-out",
                        }}
                      >
                        {dashboard.europe_insights.map((item, index) => (
                          <div key={index} className="insight-item">
                            {item.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </section>
          </div>
        </div>

        <div className="dashboard-sidebar midSidebar">
          <div className="showDesktop">
            <section className="insights">
              <div className="rocket">
                <img src={imageList.rocket} alt="Insights" />
              </div>

              <div className="insights-title">Europe now</div>
              <div className="insights-textList">
                {dashboard?.europe_insights &&
                  dashboard.europe_insights.length > 0 && (
                    <div className="insights-carousel">
                      <div
                        className="insights-carousel-track"
                        style={{
                          transform: `translateX(-${currentInsightIndex * 100}%)`,
                          transition: "transform 0.5s ease-in-out",
                        }}
                      >
                        {dashboard.europe_insights.map((item, index) => (
                          <div key={index} className="insight-item">
                            {item.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* ── Blog Section ──────────────────────────────────────────────────────── */}
      {!blogLoading && blogPosts.length > 0 && (
        <div className="blog-section">
          <div className="blog-section-header">
            <h3>Latest from the blog</h3>
            <div className="navigation-arrows">
              <Button
                icon="pi pi-chevron-left"
                text
                className="nav-arrow"
                onClick={handlePreviousBlog}
                disabled={totalBlogSlides <= 1}
              />
              <Button
                icon="pi pi-chevron-right"
                text
                className="nav-arrow"
                onClick={handleNextBlog}
                disabled={totalBlogSlides <= 1}
              />
            </div>
          </div>

          <div className="blog-carousel">
            <div className="blog-track">
              {blogPosts
                .slice(
                  currentBlogIndex * blogCardsPerSlide,
                  (currentBlogIndex + 1) * blogCardsPerSlide,
                )
                .map((post) => (
                  <a
                    key={post.id}
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="blog-card"
                  >
                    <div className="blog-card-image">
                      <img src={post.image} alt={post.title} />
                      <div className="blog-card-image-meta">
                        <span className="blog-date">{post.date.display}</span>
                        <span className="blog-category">
                          {post.category.name}
                        </span>
                      </div>
                    </div>
                    <div className="blog-card-body">
                      <h4
                        className="blog-card-title"
                        dangerouslySetInnerHTML={{ __html: post.title }}
                      />
                      <p
                        className="blog-card-excerpt"
                        dangerouslySetInnerHTML={{ __html: post.excerpt }}
                      />
                      <span className="blog-read-more">
                        Read post <i className="pi pi-arrow-up-right" />
                      </span>
                    </div>
                  </a>
                ))}
            </div>
          </div>
        </div>
      )}

      <ProfileViewsModal
        isOpen={isProfileViewsModalOpen}
        onClose={() => setIsProfileViewsModalOpen(false)}
        userViews={userViews}
        loading={userViewsLoading}
        totalViews={dashboard?.profile_views || 0}
      />
      <CreateGoalModal
        visible={isCreateModalOpen}
        onHide={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
};
